import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Zap, MessageSquare, BarChart3, Database, Link2, Users, Sparkles } from 'lucide-react';
import NavigationControls from '../components/NavigationControls';
import PartieBadge from '../components/PartieBadge';
import { useLanguage } from '../contexts/LanguageContext';
import { piModulesFR, piModulesEN, piTextsFR, piTextsEN } from '../piTranslations';

const iconMap = { Database, Shield, Zap, Link2, MessageSquare, BarChart3, Users, Sparkles };

export default function ProprieteIntellectuellePage() {
  const { language } = useLanguage();
  const modules = language === 'fr' ? piModulesFR : piModulesEN;
  const t = language === 'fr' ? piTextsFR : piTextsEN;
  
  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        <PartieBadge path="/propriete-intellectuelle" />
        
        <Card className="bg-black/60 border-cyan-500/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl sm:text-3xl lg:text-4xl text-white mb-2">
              {t.titre}
            </CardTitle>
            <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-cyan-500 to-lime-500 rounded-full" />
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Intro */}
            <div className="p-6 bg-gradient-to-br from-cyan-500/15 to-transparent rounded-xl border border-cyan-500/30">
              <p className="text-gray-200 text-lg leading-relaxed">
                {t.intro}
              </p>
            </div>

            {/* Valuation Section */}
            <div className="p-6 bg-gradient-to-br from-orange-500/15 to-transparent rounded-xl border-2 border-orange-500/40">
              <h3 className="text-2xl font-bold text-orange-400 mb-3">{t.valuationTitre}</h3>
              <p className="text-gray-300 mb-4">{t.valuationIntro}</p>
              <div className="space-y-3">
                <div className="p-4 bg-black/40 rounded-lg border border-orange-500/30">
                  <p className="text-sm text-gray-400 mb-1">{language === 'fr' ? 'Coût de reconstruction – noyau de PI actuelle matérialisée' : 'Reconstruction cost – current materialized IP core'}</p>
                  <p className="text-xl font-bold text-orange-400">{language === 'fr' ? '8 850 heures' : '8,850 hours'}</p>
                  <p className="text-lg font-semibold text-orange-300">{language === 'fr' ? '1,15M – 1,24M CAD' : '$1.15M – 1.24M CAD'}</p>
                </div>
                <div className="p-4 bg-black/40 rounded-lg border border-cyan-500/30">
                  <p className="text-sm text-gray-400 mb-1">{language === 'fr' ? 'Modules complémentaires d\'intelligence opérationnelle' : 'Operational intelligence complementary modules'}</p>
                  <p className="text-xl font-bold text-cyan-400">{language === 'fr' ? '1 000 heures' : '1,000 hours'}</p>
                  <p className="text-lg font-semibold text-cyan-300">{language === 'fr' ? '125k – 150k CAD' : '$125k – 150k CAD'}</p>
                </div>
                <div className="p-4 bg-gradient-to-r from-lime-500/20 to-lime-500/5 rounded-lg border-2 border-lime-500/40">
                  <p className="text-sm text-gray-400 mb-1">{language === 'fr' ? 'Coût de reconstruction – total élargi' : 'Reconstruction cost – expanded total'}</p>
                  <p className="text-xl font-bold text-lime-400">{language === 'fr' ? '9 850 heures' : '9,850 hours'}</p>
                  <p className="text-lg font-semibold text-lime-300">{language === 'fr' ? '1,275M – 1,39M CAD' : '$1.275M – 1.39M CAD'}</p>
                </div>
              </div>
            </div>

            {/* Composantes Intégrées */}
            <div>
              <h3 className="text-xl font-bold text-cyan-400 mb-6">{t.composantesTitre}</h3>
              <div className="space-y-4">
                {modules.map((item, idx) => {
                  const Icon = iconMap[item.icon];
                  return (
                    <div key={idx} className="p-5 bg-black/40 rounded-lg border border-cyan-500/20 hover:border-cyan-500/40 transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-cyan-500/20 rounded-lg flex-shrink-0">
                          <Icon className="h-6 w-6 text-cyan-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-white font-semibold">{item.titre}</h4>
                            <div className="flex flex-col items-end ml-4">
                              <span className="text-xs text-gray-500">{item.temps}</span>
                              <span className="text-xs font-semibold text-cyan-400">{item.cout}</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-300 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Infrastructure */}
              <div className="mt-4 p-4 bg-black/40 rounded-lg border border-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">{language === 'fr' ? 'Infrastructure, sécurité, DevOps, QA, documentation' : 'Infrastructure, security, DevOps, QA, documentation'}</span>
                  <div className="flex flex-col items-end ml-4">
                    <span className="text-xs text-gray-500">{language === 'fr' ? '1 675 heures' : '1,675 hours'}</span>
                    <span className="text-xs font-semibold text-cyan-400">{language === 'fr' ? '217,75k – 234,5k $' : '$217.75k – 234.5k'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Données Propriétaires */}
            <div className="p-6 bg-gradient-to-br from-lime-500/10 to-transparent rounded-xl border border-lime-500/30">
              <h3 className="text-xl font-bold text-lime-400 mb-3">{t.donneesTitre}</h3>
              <p className="text-gray-300 leading-relaxed">
                {t.donneesDesc}
              </p>
            </div>

            {/* Phrase de clôture officielle */}
            <div className="p-4 bg-black/40 rounded-lg border-l-4 border-cyan-500">
              <p className="text-sm text-gray-400 italic">
                {t.cloture}
              </p>
            </div>

            <NavigationControls />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
